import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { SponsorLocale } from '@/lib/contract'
import { SponsorApiError } from '@/lib/api'
import { pushConfigQuery } from '@/lib/queries'
import { deviceLocale, deviceSupport, disableDevice, enableDevice, releaseDevice, syncDevice, testDevice, type DeviceState } from './device'

/* Spec PWA-014 D-09 (copia do partner-app, PARTNER-002 D-10) — situacao dos avisos neste aparelho, para o cartao
   "Avisos neste aparelho" e o convite da tela Mensagens.
   unavailable: servidor sem chaves · own-account: impersonacao ("Ver como padrinho") · unsupported: navegador sem push
   · needs-install: iPhone/iPad fora do app instalado · denied: bloqueado no navegador · off / on. */

export type PushDeviceStatus = 'loading' | 'unavailable' | 'own-account' | DeviceState | 'unsupported' | 'needs-install'

/** Uma sincronizacao por carga da pagina e por idioma (o cartao, o convite e o layout nao repetem o PUT). */
let synced: { locale: SponsorLocale; result: Promise<DeviceState> } | null = null
function sync(publicKey: string, locale: SponsorLocale) {
    if (!synced || synced.locale !== locale) synced = { locale, result: syncDevice(publicKey, locale).catch(() => 'off' as const) }
    return synced.result
}
const settle = (state: DeviceState) => { synced = { locale: deviceLocale(), result: Promise.resolve(state) } }

/** Sair (D-11): apaga a inscricao deste aparelho e a proxima pessoa neste navegador comeca do zero. */
export async function releasePushDevice() {
    await releaseDevice()
    synced = null
}

export function usePushDevice() {
    const { i18n } = useTranslation()
    const config = useQuery(pushConfigQuery)
    const [support] = useState(deviceSupport)
    const [state, setState] = useState<DeviceState | null>(null)
    const publicKey = config.data?.enabled ? config.data.publicKey : null
    const ownAccount = config.error instanceof SponsorApiError && config.error.code === 'SPONSOR_PUSH_OWN_ACCOUNT'

    useEffect(() => {
        if (!publicKey || support !== 'supported') return
        let alive = true
        void sync(publicKey, deviceLocale()).then(s => alive && setState(s))
        return () => { alive = false }
    }, [publicKey, support, i18n.language])

    const enable = useMutation({
        mutationFn: () => enableDevice(publicKey!, deviceLocale()),
        onSuccess: (s) => {
            settle(s)
            setState(s)
        },
    })
    const disable = useMutation({
        mutationFn: disableDevice,
        onSuccess: (s) => {
            settle(s)
            setState(s)
        },
    })
    const test = useMutation({
        mutationFn: testDevice,
        onError: (error) => {
            // O servidor nao conhece mais este aparelho (cancelado pelo navegador): pede para ligar de novo
            if (error instanceof SponsorApiError && (error.code === 'SPONSOR_PUSH_GONE' || error.code === 'SPONSOR_PUSH_NOT_FOUND')) setState('off')
        },
    })

    const status: PushDeviceStatus = config.isPending ? 'loading'
        : ownAccount ? 'own-account'
            : !publicKey ? 'unavailable'
                : support !== 'supported' ? support
                    : state ?? 'loading'

    return { status, configError: ownAccount ? null : config.error, enable, disable, test }
}

/** Sincroniza este aparelho ao abrir a area com sessao (AuthLayout) e quando o idioma muda, sem mostrar nada (D-04). */
/** `enabled` false: nao inscreve este aparelho (visualizacao do atendimento, que so le). */
export function usePushSync(enabled = true) {
    const { i18n } = useTranslation()
    const config = useQuery({ ...pushConfigQuery, enabled })
    const publicKey = enabled && config.data?.enabled ? config.data.publicKey : null
    useEffect(() => {
        if (publicKey && deviceSupport() === 'supported') void sync(publicKey, deviceLocale())
    }, [publicKey, i18n.language])
}
